import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cn } from '../cn.ts';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  /** Buttons/links shown on the right of the header. */
  actions?: ReactNode;
  /** Remove the body padding (for tables and lists that reach the edges). */
  flush?: boolean;
}

/** The dashboard's CardLayout: rounded-xl, solid-2 fill, 1px container outline. */
export function Card({
  title,
  description,
  actions,
  flush = false,
  className,
  children,
  ...rest
}: CardProps) {
  const hasHeader = Boolean(title || description || actions);
  return (
    <section
      className={cn(
        'flex flex-col w-full min-w-0 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container',
        className,
      )}
      {...rest}
    >
      {hasHeader && (
        <header className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
          <div className="flex flex-col min-w-0 gap-0.5">
            {title && <h2 className="text-heading-2 text-n-slate-12 m-0">{title}</h2>}
            {description && <p className="text-body-main text-n-slate-11 m-0">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </header>
      )}
      <div className={cn(!flush && (hasHeader ? 'px-5 pb-5' : 'p-5'))}>{children}</div>
    </section>
  );
}

export type BadgeColor = 'slate' | 'blue' | 'ruby' | 'amber' | 'teal' | 'iris';

const BADGE_COLORS: Record<BadgeColor, string> = {
  slate: 'bg-n-slate-9/10 text-n-slate-11',
  blue: 'bg-n-brand/10 text-n-blue-11',
  ruby: 'bg-n-ruby-9/10 text-n-ruby-11',
  amber: 'bg-n-amber-9/15 text-n-amber-11',
  teal: 'bg-n-teal-9/10 text-n-teal-11',
  iris: 'bg-n-iris-9/10 text-n-iris-11',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  color?: BadgeColor;
}

export function Badge({ color = 'slate', className, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-label-small whitespace-nowrap',
        BADGE_COLORS[color],
        className,
      )}
      {...rest}
    />
  );
}

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 px-6 py-12 text-center', className)}>
      {icon && (
        <div className="flex items-center justify-center size-10 rounded-xl bg-n-alpha-2 text-n-slate-11 [&_svg]:size-5">
          {icon}
        </div>
      )}
      <div className="flex flex-col gap-1 max-w-sm">
        <h3 className="text-heading-2 text-n-slate-12 m-0">{title}</h3>
        {description && <p className="text-body-main text-n-slate-11 m-0">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Primary actions, right-aligned. */
  actions?: ReactNode;
  /** Something above the title, e.g. a back link or breadcrumb. */
  eyebrow?: ReactNode;
  className?: string;
}

/** Page title bar, sized like the dashboard's settings/contacts headers. */
export function PageHeader({ title, description, actions, eyebrow, className }: PageHeaderProps) {
  return (
    <header className={cn('flex flex-wrap items-end justify-between gap-4 pb-6', className)}>
      <div className="flex flex-col min-w-0 gap-1">
        {eyebrow}
        <h1 className="text-heading-1 text-n-slate-12 m-0 truncate">{title}</h1>
        {description && <p className="text-body-main text-n-slate-11 m-0">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}

// ---- Table ------------------------------------------------------------------------------------

export function Table({ className, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table
        className={cn('min-w-full table-auto border-collapse divide-y divide-n-weak', className)}
        {...rest}
      />
    </div>
  );
}

export function THead({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn(className)} {...rest} />;
}

export function TBody({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('divide-y divide-n-weak text-n-slate-11', className)} {...rest} />;
}

export function TR({ className, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('transition-colors', className)} {...rest} />;
}

export function TH({ className, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn(
        'py-3 pe-4 first:ps-0 text-start text-heading-3 text-n-slate-12 whitespace-nowrap',
        className,
      )}
      {...rest}
    />
  );
}

export function TD({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('py-3 pe-4 text-body-main align-middle', className)} {...rest} />;
}
